import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../../theme/sbgTokens';
import type { TimeOffCategory, TimeOffType } from '../../../types/timeOff';
import { categoryChipStyle, pendingBorder, SeriesChipStyle, timeOffTypeChipStyle } from '../categoryChipStyle';

/**
 * A quiet categorical chip (book §11.11 tag, §13.3 color): a 22px pill on the
 * series tint with a 6px series-colored dot and primary text. For categories
 * and types only — true states use `StatusPill`.
 */
export const SeriesChip: React.FC<{
  tone: SeriesChipStyle;
  children?: React.ReactNode;
  /**
   * Pending entries: surface fill, a 1px dashed outline in the full series
   * color and a hollow dot (all ≥3:1), so the state never rests on a faint tint.
   */
  pending?: boolean;
  title?: string;
  sx?: SxProps<Theme>;
}> = ({ tone, children, pending = false, title, sx }) => (
  <Box
    component="span"
    title={title}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 22,
      maxWidth: '100%',
      px: 1,
      borderRadius: sbg.radius.pill,
      border: pending ? pendingBorder(tone) : `1px solid ${tone.border}`,
      bgcolor: pending ? sbg.bg.surface : tone.bg,
      color: tone.color,
      font: 'var(--sbg-type-label-xs)',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      flexShrink: 0,
      ...sx,
    }}
  >
    <SeriesDot color={tone.dot} hollow={pending} />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
      {children ?? tone.label}
    </Box>
  </Box>
);

/** The 6px series dot; hollow for pending. */
export const SeriesDot: React.FC<{ color: string; hollow?: boolean; size?: number }> = ({ color, hollow = false, size = 6 }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      width: size,
      height: size,
      flex: 'none',
      borderRadius: '50%',
      bgcolor: hollow ? 'transparent' : color,
      boxShadow: hollow ? `inset 0 0 0 1.5px ${color}` : 'none',
    }}
  />
);

/**
 * A category or type as plain text with its 6px series dot — the quietest
 * form, for dense tables where a row already carries other badges (book §16:
 * never three colored badges in one row).
 */
export const SeriesLabel: React.FC<{ tone: SeriesChipStyle; children?: React.ReactNode; muted?: boolean; sx?: SxProps<Theme> }> = ({
  tone,
  children,
  muted = false,
  sx,
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 0,
      maxWidth: '100%',
      color: muted ? sbg.text.secondary : sbg.text.primary,
      font: 'var(--sbg-type-body-sm)',
      verticalAlign: 'middle',
      ...sx,
    }}
  >
    <SeriesDot color={tone.dot} />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
      {children ?? tone.label}
    </Box>
  </Box>
);

export const CategoryChip: React.FC<{ category?: TimeOffCategory | string | null; sx?: SxProps<Theme> }> = ({ category, sx }) => (
  <SeriesChip tone={categoryChipStyle(category)} sx={sx} />
);

export const TimeOffTypeChip: React.FC<{ type?: TimeOffType | string | null; pending?: boolean; sx?: SxProps<Theme> }> = ({
  type,
  pending,
  sx,
}) => <SeriesChip tone={timeOffTypeChipStyle(type)} pending={pending} sx={sx} />;
